import { Box, Chip, Typography, alpha, useTheme } from '@mui/material'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { formatDateString } from 'utils/formatDayjsDateTime'
import type { AgreementFamily, VersionedDeal } from 'utils/agreementVersioning'

const TERMINAL_STATUSES = ['Ended', 'Cancelled', 'Replaced']

const getStatusInfo = (deal: any) => {
  const stage = deal?.stage
  const status = deal?.dealStatus
  if (stage === 'Deal Won' && !TERMINAL_STATUSES.includes(status)) {
    return { label: 'Active', color: 'rgb(80, 200, 120)' }
  }
  if (status === 'Replaced') return { label: 'Replaced', color: 'rgb(239, 195, 124)' }
  if (status === 'Ended') return { label: 'Ended', color: 'rgba(220, 20, 60, 0.9)' }
  if (status === 'Cancelled') return { label: 'Cancelled', color: 'rgba(110, 114, 132, 0.5)' }
  return { label: status || stage || 'In Progress', color: renderColorByString(stage) }
}

type VersionTimelineProps = {
  versions: VersionedDeal[]
  selectedIndex: number
  agreementFamily: AgreementFamily
  onSelect: (index: number) => void
}

const VersionTimeline = ({ versions, selectedIndex, agreementFamily, onSelect }: VersionTimelineProps) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        width: 280,
        minWidth: 280,
        borderRight: '1px solid',
        borderColor: 'divider',
        overflowY: 'auto',
        py: 1,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
          Version History
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          {versions.length} version{versions.length !== 1 ? 's' : ''} &middot; {agreementFamily}
        </Typography>
      </Box>

      {/* Version items — newest first */}
      {[...versions].reverse().map((v, reversedIdx) => {
        const actualIndex = versions.length - 1 - reversedIdx
        const isSelected = actualIndex === selectedIndex
        const isLatest = actualIndex === versions.length - 1
        const statusInfo = getStatusInfo(v.deal)

        return (
          <Box
            key={v.deal.deal_uuid}
            onClick={() => onSelect(actualIndex)}
            sx={{
              position: 'relative',
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              borderLeft: '3px solid',
              borderLeftColor: isSelected ? 'primary.main' : 'transparent',
              bgcolor: isSelected
                ? alpha(theme.palette.text.primary, 0.04)
                : 'transparent',
              '&:hover': {
                bgcolor: alpha(theme.palette.text.primary, 0.06),
              },
              transition: 'all 0.15s ease',
            }}
          >
            {/* Connector line */}
            {reversedIdx < versions.length - 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 26,
                  bottom: 0,
                  top: '100%',
                  width: 2,
                  height: 0,
                  bgcolor: 'divider',
                }}
              />
            )}

            {/* Version number + latest badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip
                label={`v${v.semanticVersion}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  bgcolor: isSelected ? 'primary.main' : 'action.selected',
                  color: isSelected ? 'primary.contrastText' : 'text.primary',
                }}
              />
              {isLatest && (
                <Chip
                  label="Latest"
                  size="small"
                  variant="outlined"
                  color="success"
                  sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                />
              )}
            </Box>

            {/* Deal type */}
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary', mb: 0.25 }}>
              {v.deal.type}
            </Typography>

            {/* Date range */}
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {formatDateString(v.deal.startDate)} — {formatDateString(v.deal.endDate)}
            </Typography>

            {/* Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: statusInfo.color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 11, color: statusInfo.color, fontWeight: 500 }}>
                {statusInfo.label}
              </Typography>
            </Box>

            {/* Product count */}
            <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>
              {v.products.length} service{v.products.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

export default VersionTimeline
